import { forwardRef } from "react";
import { Pagination } from "./pagination";
import Rows from "./rows";
import { twMerge } from "tailwind-merge";
import { UseTableSelect } from "@/common/hooks/useTableSelect";
import SelectAllCheckbox from "./selectAllCheckbox";
import { EmptyDataMessage } from "@/common/components/EmptyDataMessage";
import { moveActionsColumnFirst } from "@/common/utils/moveActionsColumnFirst";
import { cn } from "@/common/utils/cn";

export interface ColumnPattern {
  label: string;
  value: string;
  className?: string;
  hidden?: boolean;
}

export interface TableProps {
  className?: string;
  headerClassName?: string;
  headerCellClassName?: string;
  columns: ColumnPattern[];
  rows: { [key: string]: unknown }[];
  page: number;
  totalPages: number;
  totalCount?: number;
  setPage?: React.Dispatch<React.SetStateAction<number | undefined>>;
  isLoadingData: boolean;
  loadingRows?: number;
  showEmptyTable?: boolean;
  hasPagination?: boolean;
  bottomContent?: JSX.Element;
  rowEmptyValue?: string;
  hasSelection?: boolean;
  keyExtractor?: string;
  hasSelectAll?: boolean;
  tableSelect?: UseTableSelect;
  style?: React.CSSProperties;
  stripedRows?: boolean;
  emptyDataClassName?: string
  emptyDataImageHeight?: number
}

const Table = forwardRef<HTMLDivElement, TableProps>(
  (
    {
      className,
      headerClassName,
      headerCellClassName,
      columns,
      rows,
      page,
      totalPages,
      totalCount,
      setPage,
      isLoadingData,
      loadingRows,
      showEmptyTable,
      hasPagination = false,
      bottomContent = null,
      rowEmptyValue,
      tableSelect,
      hasSelectAll = true,
      style,
      stripedRows = true,
      emptyDataClassName,
      emptyDataImageHeight
    },
    ref
  ) => {
    const columnsWithActionsFirst = moveActionsColumnFirst(columns);
    const visibleColumns = columnsWithActionsFirst.filter((column) => !column.hidden);

    return isLoadingData || !!rows.length || showEmptyTable ? (
      <div
        className={twMerge(
          "flex flex-col max-h-[73vh] rounded-lg overflow-hidden",
          className
        )}
        style={style}
      >
        <div className="overflow-auto" ref={ref}>
          <table className="text-sm w-full whitespace-nowrap">
            <thead
              className={twMerge(
                "bg-[#212023] sticky top-0 z-10",
                headerClassName
              )}
            >
              <tr className="[&>*:first-child]:rounded-tl-lg [&>*:first-child]:border-l-0 ">
                {tableSelect && hasSelectAll && (
                  <SelectAllCheckbox tableSelect={tableSelect} />
                )}

                {visibleColumns.map((column) => (
                  <th
                    className={twMerge(
                      "text-left px-4 border-x z-30 border-x-[#64676980] py-6 text-sm font-bold",
                      headerCellClassName
                    )}
                    key={column.value}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody
              className={twMerge(
                stripedRows
                  ? "[&>*:nth-child(odd)]:bg-white-100 [&>*:nth-child(even)]:bg-blue-100"
                  : ""
              )}
            >
              <Rows
                data={rows}
                columns={visibleColumns}
                isLoading={isLoadingData}
                loadingRows={totalPages > 1 ? 50 : loadingRows}
                showEmptyTable={showEmptyTable}
                emptyValue={rowEmptyValue}
                tableSelect={tableSelect}
              />
            </tbody>
          </table>
        </div>
        <div
          className={twMerge(
            "w-full bg-black-300 rounded-b-lg flex justify-between items-center",
            rows?.length > 0 && "px-4 py-2",
            typeof totalCount === "undefined" && "justify-end"
          )}
        >
          {typeof totalCount !== "undefined" ? (
            <p className="text-xs italic font-light">
              Mostrando {rows.length} resultados de {totalCount}
            </p>
          ) : null}
          {hasPagination && setPage ? (
            <Pagination
              currentPage={Number(page)}
              setPage={setPage}
              totalPages={totalPages}
            />
          ) : (
            bottomContent
          )}
        </div>
      </div>
    ) : (
      <EmptyDataMessage className={cn(className, emptyDataClassName)} imageHeight={emptyDataImageHeight} />
    );
  }
);

export default Table;
